import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { useConfirm } from 'hooks/useConfirm';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doSettingsIntegrationRemove,
  doSettingsIntegrationsLoad,
  NAVIGATE_MODAL,
} from 'store/actions';
import {
  selectUserIsSupport,
  selectUserSelectedOrganisation,
  selectUserSelectedOrganisationFeature,
} from 'store/auth/selectors';
import {
  selectSettingsIntegrations,
  selectSettingsIntegrationsLoadStatus,
} from 'store/remote/selectors';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsIntegrations
>;

export const IntegrationsSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const screenSize = useScreenSize();
  const organisation = useSelector(selectUserSelectedOrganisation);
  const isSupport = useSelector(selectUserIsSupport);
  const hasFeature = useSelector((state) =>
    selectUserSelectedOrganisationFeature(state, 'manageIntegrations'),
  );
  const loadStatus = useSelector(selectSettingsIntegrationsLoadStatus);
  const integrations = useSelector(selectSettingsIntegrations);

  const canManage = !!organisation && isSupport && hasFeature;

  React.useEffect(() => {
    if (!canManage) {
      navigation.goBack();
      return;
    }
    dispatch(doSettingsIntegrationsLoad({ organisationId: organisation.id }));
  }, [canManage, dispatch, navigation, organisation]);

  if (!canManage) {
    return null;
  }

  const integrationList = Object.values(integrations);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Integrations"
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          onPressSaveButton={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.SettingsIntegrationAdd,
                params: { organisationId: organisation.id },
              }),
            )
          }
          saveButtonIcon={ICONS.PlusLight}
        />
      }
    >
      {loadStatus === 'loaded' && integrationList.length === 0 && (
        <PageEmpty
          icon={ICONS.PlugLight}
          text="No Integrations"
          button={{
            text: 'Add Integration',
            onPress: () =>
              dispatch(
                NAVIGATE_MODAL({
                  screen: ModalScreens.SettingsIntegrationAdd,
                  params: { organisationId: organisation.id },
                }),
              ),
          }}
        />
      )}
      {loadStatus === 'loaded' && integrationList.length > 0 && (
        <Card explanation="Integration settings apply to this organisation only.">
          {integrationList.map((integration) => (
            <CardRow
              key={integration.uid}
              icon={ICONS.PlugLight}
              label={
                integration.integrationName || integration.integrationPartner
              }
              label2={`[${integration.integrationPartner}] · ${
                integration.enabled ? 'Enabled' : 'Disabled'
              }`}
              label2NumberOfLines={2}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SettingsIntegrationEdit,
                    params: {
                      organisationId: organisation.id,
                      integrationId: integration.uid,
                    },
                  }),
                )
              }
              menuOptions={[
                {
                  text: 'Delete integration',
                  destructive: true,
                  onPress: () =>
                    confirm({
                      title: 'Delete Integration',
                      text: `Delete ${
                        integration.integrationName ||
                        integration.integrationPartner
                      }? This also removes its options and secrets.`,
                      confirmText: 'Delete',
                      destructive: true,
                      delayedConfirm: true,
                      onConfirm: () =>
                        dispatch(
                          doSettingsIntegrationRemove({
                            organisationId: organisation.id,
                            integrationId: integration.uid,
                          }),
                        ),
                    }),
                },
              ]}
            />
          ))}
        </Card>
      )}
      {loadStatus === 'loading' && (
        <View
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <ActivityIndicator size={56} />
        </View>
      )}
      {loadStatus === 'error' && (
        <PageEmpty
          icon={ICONS.ExclamationSolid}
          text="Unable to Load Integrations"
          button={{
            text: 'Try Again',
            onPress: () =>
              dispatch(
                doSettingsIntegrationsLoad({
                  organisationId: organisation.id,
                }),
              ),
          }}
        />
      )}
    </Page>
  );
};
